<script setup>
import Spinner from '@/components/shared/core/Spinner.vue'
import TransitionFadeIn from '@/components/shared/transitions/TransitionFadeIn.vue'
import ImageLightBox from '@/components/shared/image/ImageLightBox.vue'
import ImageDownloadButton from '@/components/shared/buttons/ImageDownloadButton.vue'
import Tooltip from '@/components/shared/core/Tooltip.vue'
import {useImage} from '@/composables/use-image.js'
import {useValueToggle} from '@/composables/use-value-toggle.js'
import {useImageDownload} from '@/composables/use-image-download.js'
import {toRefs, watch} from 'vue'

//Emits and props
const emit = defineEmits([
  'loaded',
])
const props = defineProps({
  src: {
    type: [String, File],
    required: true,
  },
  alt: {
    type: String,
    default: '',
  },
  isImageKey: {
    type: Boolean,
    default: false,
  },
  canZoom: {
    type: Boolean,
    default: false,
  },
  canDownload: {
    type: Boolean,
    default: false,
  },
})

//Image handling
const {src, isImageKey, canZoom} = toRefs(props)
const {isLoading, hasLoaded, image} = useImage(src, isImageKey)
const {downloadImage} = useImageDownload()

//Zoom toggle
const {
  isValue: isShowingZoom,
  setTrue: showZoom,
  setFalse: hideZoom,
} = useValueToggle()

//Click handling
const handleClick = () => {
  if (canZoom.value) {
    showZoom()
  }
}

//Emit when loaded
watch(hasLoaded, () => {
  if (hasLoaded.value) {
    emit('loaded')
  }
}, {immediate: true})
</script>

<template>
  <div
    v-if="src"
    :class="$style.card"
  >
    <Spinner
      v-if="!image || isLoading"
      :class="$style.spinner"
    />
    <TransitionFadeIn>
      <img
        v-if="image"
        :src="image.src"
        :alt="alt"
        :role="alt ? 'img' : 'presentation'"
        :class="{
          [$style.image]: true,
          [$style.zoomable]: canZoom,
        }"
        @click="handleClick()"
      >
    </TransitionFadeIn>
    <div
      v-if="canDownload"
      :class="$style.buttons"
    >
      <Tooltip
        text="Download image"
        :offset="-10"
        no-wrap
      >
        <ImageDownloadButton
          :class="$style.download"
          @click.stop="downloadImage(src)"
        />
      </Tooltip>
    </div>
    <ImageLightBox
      v-if="canZoom && image"
      :is-visible="isShowingZoom"
      :src="src"
      @download="downloadImage(src)"
      @close="hideZoom()"
    />
  </div>
</template>

<style module lang="scss">
.card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: fit-content;
  &:hover {
    .download {
      opacity: 1 !important;
    }
  }
}
.buttons {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}
.download {
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}
.image {
  border-radius: var(--border-radius-m);
  &.zoomable {
    cursor: zoom-in;
  }
}
.spinner {
  margin: 1rem;
}
</style>
